import { ApplicationConfig, importProvidersFrom, APP_INITIALIZER, Injector } from '@angular/core';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { MsalModule, MsalService, MsalGuard, MsalInterceptor, MsalBroadcastService } from '@azure/msal-angular';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { makeMsalInstance, makeMsalGuardConfig, makeMsalInterceptorConfig } from './app/auth/msal.config';

import { appRoutes } from './app.routes';
import { environment } from './environments/environment';
import { AuthService } from './app/features/auth/auth.service';
import { backendTokenInterceptor } from './app/core/interceptors/backend-token.interceptor';
import { ConfirmationService, MessageService } from 'primeng/api';

// 🎨 PrimeNG Theme (como era antes)
import Aura from '@primeng/themes/aura';
import {  providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}' // remova esta linha se sua versão não tiver 950
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      MsalModule.forRoot(
        makeMsalInstance(environment),
        makeMsalGuardConfig(environment),
        makeMsalInterceptorConfig(environment)
      )
    ),

    // ✅ Inicializa MSAL → processa redirect (entrega result pro AuthService) → sobe listeners do AuthService
    {
      provide: APP_INITIALIZER,
      useFactory: (msal: MsalService, injector: Injector) => () =>
        msal.instance
          .initialize()
          .then(() => msal.instance.handleRedirectPromise())
          .then((result) => {
            const auth = injector.get(AuthService);
            if (result) auth.ingestMsalResult(result); // garante que o id_token seja trocado no backend
            auth.bootAfterMsal();                       // depois sobe as assinaturas
          })
          .catch((e) => console.warn('MSAL init/handleRedirectPromise falhou', e)),
      deps: [MsalService, Injector],
      multi: true
    },

    provideRouter(
      appRoutes,
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
      withEnabledBlockingInitialNavigation()
    ),

    // ⚠️ Se @angular/common < 17.3, remova withInterceptors() e registre via HTTP_INTERCEPTORS tradicional
    provideHttpClient(
      withInterceptorsFromDi(),
      withFetch(),
      withInterceptors([backendTokenInterceptor])
    ),

    provideAnimationsAsync(),
 providePrimeNG({ theme: { preset: MyPreset, options: { darkModeSelector: '.app-dark' } } }),


    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MessageService,
    ConfirmationService
  ]
};
